import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Dandelion - a seed head against the golden evening light. Over the track
// its seeds let go one by one and drift away on a slow breeze, until only the
// bare stem is left. Ambient, acoustic folk, piano, neo-classical,
// contemplative music. Rules of kiosk-app/CLAUDE.md (R1-R17); danceability
// 0.16 = R9's calmest band: nothing visible on the beat, ever.
//
// THE PIECES, one draw call per category (R11 / the brief):
//   background  one quad: brown at the bottom to amber and gold above, 6 very
//               wide, very soft bokeh discs at 10 %, the cover's palette at
//               25 % at most; mean luminance well under 0.25 (R10)
//   stem        a ribbon whose curve is computed in its vertex shader: black
//               with a rim of light on the side facing the light (R13)
//   receptacle  a quad: a dark core while the head is full, a small pale ball
//               when it is bare
//   dark        akenes and beaks: lines (instanced, 2 per seed), dark
//   pappus      40-60 filaments per seed (instanced lines, ADDITIVE)
//   branches    tier 2: two tiny barbs on each filament (instanced, additive)
//
// EVERY LINE IS 1 PX (R1): a 2 px quad expanded in screen space, its profile a
// 1 px triangle filter; a filament thinner than a pixel keeps the pixel and
// loses intensity in proportion. Nothing is uploaded per frame but uniforms;
// each line knows its seed and its place on the seed (static attributes), and
// the seed's state is computed in the vertex shader: its direction on the
// Fibonacci sphere, its spin, and - once detached - its ANALYTIC flight from
// the time it let go, read from a 256 x 1 texture written only when a seed
// detaches.
//
// THE BACKLIGHT - the decisive detail. The light is behind the head, high on
// the left (L). A pappus filament glows like silk in proportion to how
// across the light it lies (1 - |t.L|), more on the head's rim on the light's
// side and on the far half (the light comes through it), golden. The core,
// the akenes and the beaks stay dark. Flying seeds pick up more light in
// front of the brightest part of the background.
//
// THE FLIGHT, per seed, tau seconds after it let go: an initial push along its
// direction on the sphere that dies in 2 s, the breeze (left and slightly up)
// that takes over in 4 s, a slow lift, a slow undulation, the axis turning
// pappus-up, a slow spin; some come toward the camera and grow out of focus
// (wider, fainter filaments: the same energy). Off screen in 30-60 s.
//
// THE COUNT follows the progress (0 at the start, all of them at 95 %),
// modulated by the energy smoothed over 8 bars, never decreasing; the order
// is the windward side first (the breeze blows to the left: the right side
// goes first), then all round.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'dandelion',
  family: 'organic',
  danceability: 0.16,
  requiresTier: 0,
  energyLevel: 0.08,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.10,
  tonality: 0.92,
  enabled: true
};

const TIER = {
  0: { seeds: 150, filaments: 30, branches: false },
  1: { seeds: 250, filaments: 50, branches: false },
  2: { seeds: 250, filaments: 50, branches: true }
};
const DEFAULT_TIER = 2;
const MAX_SEEDS = 256;

// --- geometry (screen height units: the frame is 2 tall) -------------------
const HEAD_R = 0.30;              // diameter 30 % of the screen height
const AKENE = [0.17, 0.29];       // along the seed, x HEAD_R
const BEAK_END = 0.80;
const PAPPUS_LEN = [0.17, 0.22];
const PAPPUS_POLAR = [52, 86];    // degrees from the seed's axis: an umbrella
const FIL_WIDTH = 0.0007;         // world width of a filament (sub-pixel: dims)
const BEAK_WIDTH = 0.0011;
const AKENE_WIDTH = 0.0045;
const STEM_WIDTH_PX = 3.0;
const LIGHT = [-0.50, 0.62, -0.60];  // from the head toward the light: behind, high left
const GOLD = [1.0, 0.76, 0.42];
const FIL_GAIN = 0.32;
const SWAY_PERIOD_S = 12;
const DONE_AT = 0.95;
const FADE_IN_S = 3;
const FREE_BPM = 72;
const SAFE_FLOOR = 0.6;
const PROGRESS_TAU = 2.0;
const RADIO_LOOP_SECONDS = 600;
const GOLDEN = Math.PI * (3 - Math.sqrt(5));

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth01(x) { const t = clamp01(x); return t * t * (3 - 2 * t); }
function hash(i, k) { const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return s - Math.floor(s); }

// the seed's direction on the Fibonacci sphere (same formula in the shader)
function seedDir(i, n) {
  const y = 1 - 2 * (i + 0.5) / n, r = Math.sqrt(1 - y * y), a = i * GOLDEN;
  return [Math.cos(a) * r, y, Math.sin(a) * r];
}

// ---------------------------------------------------------------------------
// Shaders
// ---------------------------------------------------------------------------

const BG_CHUNK = `
  uniform vec3 uTint;            // the palette's tint (<= 25 %)
  uniform vec4 uBokeh[6];        // x, y (screen units), radius, strength
  uniform float uAspectB;
  // The evening: brown low, amber then gold higher, brightest toward the
  // light (upper left); soft bokeh at 10 %. Linear colour.
  vec3 evening(vec2 s) {
    float t = clamp((s.y + 1.0) * 0.5, 0.0, 1.0);
    vec3 low = vec3(0.004, 0.002, 0.0008), mid = vec3(0.022, 0.010, 0.003), high = vec3(0.045, 0.024, 0.007);
    vec3 c = t < 0.55 ? mix(low, mid, t / 0.55) : mix(mid, high, (t - 0.55) / 0.45);
    // the light's side
    float lf = exp(-length(s - vec2(-0.9 * uAspectB, 0.9)) / 1.4);
    c += vec3(0.060, 0.034, 0.010) * lf;
    for (int i = 0; i < 6; i++) {
      vec4 b = uBokeh[i];
      float d = length(s - b.xy) / b.z;
      c += vec3(1.0, 0.78, 0.45) * b.w * exp(-d * d * 2.2);   // very wide, very soft (no rim)
    }
    return mix(c, uTint * dot(c, vec3(0.2126, 0.7152, 0.0722)) / max(dot(uTint, vec3(0.2126, 0.7152, 0.0722)), 1e-3), 0.25 * step(0.001, dot(uTint, uTint)));
  }
`;

const bgVertex = `
  varying vec2 vS;
  uniform float uAspect;
  void main() { vS = vec2(position.x * uAspect, position.y); gl_Position = vec4(position.xy, 0.9999, 1.0); }
`;
const bgFragment = `
  precision mediump float;
  uniform float uLightOnly;
  ${BG_CHUNK}
  varying vec2 vS;
  void main() {
    if (uLightOnly > 0.5) { gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0); return; }
    gl_FragColor = vec4(evening(vS), 1.0);
  }
`;

// The seed state and line expansion, shared by the three line categories.
const SEED_CHUNK = `
  uniform vec2 uHead;            // the head's centre, screen units
  uniform float uHeadR;
  uniform float uSway;           // the head's tilt, radians
  uniform float uTime;           // seconds since the track began
  uniform float uSeeds;          // seeds in this tier
  uniform sampler2D uDetach;     // per seed: detach time (RG, 1/20 s), detached (B)
  uniform float uPxUnit;
  uniform float uAspect;
  uniform float uFade;
  uniform vec3 uLight;
  attribute float aSeed;
  float h1(float i, float k) { return fract(sin(i * 127.1 + k * 311.7) * 43758.5453); }
  vec3 seedAxis(float i) {
    float y = 1.0 - 2.0 * (i + 0.5) / uSeeds, r = sqrt(max(0.0, 1.0 - y * y)), a = i * ${GOLDEN.toFixed(6)};
    return vec3(cos(a) * r, y, sin(a) * r);
  }
  vec3 rotZ(vec3 p, float a) { float c = cos(a), s = sin(a); return vec3(c * p.x - s * p.y, s * p.x + c * p.y, p.z); }
  // The seed: its origin (on the receptacle), its axis, its frame (u, v), a
  // depth scale and a blur (px) - attached, or flying since tau seconds.
  void seedState(out vec3 org, out vec3 ax, out vec3 u, out vec3 v, out float scale, out float blur, out float tauOut) {
    float i = aSeed;
    vec3 a0 = rotZ(seedAxis(i), uSway);
    vec4 dt = texture2D(uDetach, vec2((i + 0.5) / ${MAX_SEEDS.toFixed(1)}, 0.5));
    float detached = step(0.5, dt.b);
    float t0 = (dt.r * 255.0 * 256.0 + dt.g * 255.0) / 20.0;
    float tau = detached > 0.5 ? max(0.0, uTime - t0) : 0.0;
    tauOut = detached > 0.5 ? tau : -1.0;
    vec3 base = vec3(uHead, 0.0) + a0 * uHeadR * ${AKENE[0].toFixed(2)};
    // the flight (analytic): push, breeze, lift, undulation
    float sp = 0.7 + 0.6 * h1(i, 3.0);
    vec3 push = a0 * 0.10 * (1.0 - exp(-tau / 1.5));      // a short let-go (0.32 piled the seeds up above the head)
    vec2 wind = vec2(-0.055 - 0.035 * h1(i, 4.0), 0.010 + 0.010 * h1(i, 5.0)) * sp;
    vec2 drift = wind * (tau - 2.5 * (1.0 - exp(-tau / 2.5)));
    float lift = 0.010 * tau * (0.6 + 0.8 * h1(i, 6.0)) / (1.0 + tau / 25.0);
    float ph = h1(i, 7.0) * 6.283;
    vec2 wob = vec2(0.020 * sin(0.55 * tau + ph), 0.028 * sin(0.37 * tau + ph * 1.7)) * smoothstep(0.0, 3.0, tau);
    float zd = (h1(i, 8.0) < 0.22 ? 0.025 : -0.006) * tau;          // some come toward the lens
    org = base + vec3(push.xy + drift + vec2(0.0, lift) + wob, push.z + zd);
    // the axis turns pappus-up in flight, with a slow tilt
    vec3 up = normalize(vec3(0.25 * sin(0.3 * tau + ph), 1.0, 0.2 * cos(0.23 * tau + ph)));
    ax = normalize(mix(a0, up, smoothstep(0.0, 5.0, tau)));
    vec3 ref = abs(ax.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    u = normalize(cross(ref, ax)); v = cross(ax, u);
    float spin = h1(i, 9.0) * 6.283 + tau * (0.25 + 0.3 * h1(i, 10.0));
    float c = cos(spin), s = sin(spin);
    vec3 u2 = c * u + s * v; v = cross(ax, u2); u = u2;
    scale = 1.0 / max(0.25, 1.0 - org.z * 0.9);
    blur = clamp((scale - 1.0) * 7.0, 0.0, 7.0);
  }
  // screen units of a 3D point relative to the seed origin
  vec2 proj(vec3 org, vec3 p, float scale) { return org.xy + p.xy * scale; }
  // a line P0 -> P1 (screen units) as a quad of half width hw px; out: the
  // distance across in px (for the 1 px profile)
  vec4 lineVertex(vec2 P0, vec2 P1, float side, float along, float hwPx, out float acrossPx) {
    vec2 d = P1 - P0;
    vec2 n = normalize(vec2(-d.y, d.x) + vec2(1e-6));
    vec2 P = mix(P0, P1, along) + n * side * hwPx / uPxUnit;
    acrossPx = side * hwPx;
    return vec4(P.x / uAspect, P.y, 0.0, 1.0);
  }
`;

// the dark parts: akene (thicker) and beak (1 px); aKind 0 akene, 1 beak
const darkVertex = `
  precision highp float;
  ${SEED_CHUNK}
  attribute float aKind;
  varying float vAcross;
  varying float vHw;
  varying float vInt;
  varying vec2 vS;
  void main() {
    vec3 org, ax, u, v; float scale, blur, tau;
    seedState(org, ax, u, v, scale, blur, tau);
    float R = uHeadR;
    float a0 = aKind < 0.5 ? 0.0 : ${(AKENE[1] - AKENE[0]).toFixed(3)} * R;
    float a1 = aKind < 0.5 ? ${(AKENE[1] - AKENE[0]).toFixed(3)} * R : ${(BEAK_END - AKENE[0]).toFixed(3)} * R;
    vec2 P0 = proj(org, ax * a0, scale), P1 = proj(org, ax * a1, scale);
    float wPx = (aKind < 0.5 ? ${AKENE_WIDTH.toFixed(4)} : ${BEAK_WIDTH.toFixed(4)}) * uPxUnit * scale;
    float hw = max(1.0, wPx * 0.5 + 0.5) + blur * 0.5;
    vHw = hw;
    vInt = min(1.0, wPx) / (1.0 + blur * 0.5);
    float across;
    gl_Position = lineVertex(P0, P1, position.x, position.y, hw, across);
    vAcross = across;
    vS = vec2(gl_Position.x * uAspect, gl_Position.y);
  }
`;
const darkFragment = `
  precision mediump float;
  uniform highp float uFade;      // shared with the vertex stage: same precision
  uniform float uLightOnly;
  varying float vAcross;
  varying float vHw;
  varying float vInt;
  varying vec2 vS;
  void main() {
    if (uLightOnly > 0.5) discard;
    float cov = clamp(1.0 - (abs(vAcross) - (vHw - 1.0)), 0.0, 1.0) * vInt * uFade;
    // dark: covers what is behind (the background), warm-brown at most
    gl_FragColor = vec4(vec3(0.012, 0.007, 0.003) * cov, cov);
  }
`;

// the pappus: filament k of the seed (attributes aPolar, aAzim, aLen); and,
// for the branches, aAt (where along the filament) and aBranch (side)
function lightVertex(branch) {
  return `
  precision highp float;
  ${SEED_CHUNK}
  uniform vec4 uBokeh[6];
  uniform float uAspectB;
  attribute float aPolar;
  attribute float aAzim;
  attribute float aLen;
  ${branch ? 'attribute float aAt;\n  attribute float aBranch;' : ''}
  varying float vAcross;
  varying float vInt;
  varying vec3 vCol;
  varying vec2 vS;
  void main() {
    vec3 org, ax, u, v; float scale, blur, tau;
    seedState(org, ax, u, v, scale, blur, tau);
    float R = uHeadR;
    vec3 tip = ax * ${(BEAK_END - AKENE[0]).toFixed(3)} * R;
    vec3 dir = cos(aPolar) * ax + sin(aPolar) * (cos(aAzim) * u + sin(aAzim) * v);
    vec3 a = tip, b = tip + dir * aLen * R;
    ${branch ? `
    // a barb: from aAt along the filament, short, angled forward to one side
    vec3 side = normalize(cross(dir, ax) + vec3(1e-4));
    a = tip + dir * aLen * R * aAt;
    vec3 bd = normalize(dir * 0.75 + side * aBranch * 0.65);
    b = a + bd * aLen * R * 0.14;
    dir = bd;` : ''}
    vec2 P0 = proj(org, a, scale), P1 = proj(org, b, scale);
    // the silk: across the light, the rim on the light's side, the far half
    vec3 L = normalize(uLight);
    float across = 1.0 - abs(dot(dir, L));
    vec3 radial = normalize(a + b);
    // R13: the rim facing the light glows; the far side of the head stays in its own shadow
    float facing = clamp(dot(normalize(radial.xy + vec2(1e-4)), normalize(L.xy)) * 0.5 + 0.5, 0.0, 1.0);
    float rim = 0.12 + 0.88 * facing * facing;
    float back = 0.6 + 0.4 * clamp(-radial.z, 0.0, 1.0);
    float k = pow(across, 1.6) * rim * back;
    // in flight: more light in front of the bright part of the evening
    float bg = 0.0;
    if (tau >= 0.0) {
      vec2 s = org.xy;
      float lf = exp(-length(s - vec2(-0.9 * uAspectB, 0.9)) / 1.4);
      for (int i = 0; i < 6; i++) { vec4 bk = uBokeh[i]; float dd = length(s - bk.xy) / bk.z; bg += bk.w * 8.0 * exp(-dd * dd * 2.2); }
      k *= 0.8 + 1.6 * (lf + bg);
    }
    float wPx = ${FIL_WIDTH.toFixed(4)} * uPxUnit * scale;
    float hw = 1.0 + blur * 0.5;
    // sub-pixel: one pixel, dimmed in proportion; out of focus: wider, fainter
    vInt = min(1.0, wPx) * (1.0 / (1.0 + blur * 0.6)) * ${FIL_GAIN.toFixed(2)} * ${branch ? '0.6' : '1.0'} * uFade;
    vCol = vec3(${GOLD.map((v) => v.toFixed(2)).join(', ')}) * k;
    float acr;
    gl_Position = lineVertex(P0, P1, position.x, position.y, hw, acr);
    vAcross = acr / hw;
    vS = vec2(gl_Position.x * uAspect, gl_Position.y);
  }
`;
}
const lightFragment = `
  precision mediump float;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying float vAcross;
  varying float vInt;
  varying vec3 vCol;
  varying vec2 vS;
  void main() {
    float cov = max(0.0, 1.0 - abs(vAcross));           // a 1 px triangle across a 2 px quad
    vec2 q = abs(vS - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float safe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    gl_FragColor = vec4(vCol * cov * vInt * safe, 1.0);  // additive (three's: SRC_ALPHA, ONE - alpha 0 added nothing)
  }
`;

// the stem: a ribbon along a quadratic curve from the bottom to the head
const stemVertex = `
  precision highp float;
  uniform vec2 uBase;
  uniform vec2 uCtrl;
  uniform vec2 uHead;
  uniform float uPxUnit;
  uniform float uAspect;
  varying float vAcross;
  varying float vRimSide;
  void main() {
    float t = position.y;
    vec2 p = mix(mix(uBase, uCtrl, t), mix(uCtrl, uHead, t), t);
    vec2 d = normalize(2.0 * (1.0 - t) * (uCtrl - uBase) + 2.0 * t * (uHead - uCtrl));
    vec2 n = vec2(-d.y, d.x);
    float hw = ${(STEM_WIDTH_PX / 2 + 1).toFixed(1)} * (1.0 - 0.35 * t);
    vec2 P = p + n * position.x * hw / uPxUnit;
    vAcross = position.x * hw;
    vRimSide = dot(n, normalize(vec2(-0.6, 0.8))) * position.x;   // > 0 on the side facing the light
    gl_Position = vec4(P.x / uAspect, P.y, 0.0, 1.0);
  }
`;
const stemFragment = `
  precision mediump float;
  uniform float uFade;
  uniform float uLightOnly;
  varying float vAcross;
  varying float vRimSide;
  void main() {
    float hw = ${(STEM_WIDTH_PX / 2 + 1).toFixed(1)};
    float cov = clamp(${(STEM_WIDTH_PX / 2).toFixed(1)} + 0.5 - abs(vAcross), 0.0, 1.0) * uFade;
    // R13: a black body, only the edge facing the light lit (1.5 px)
    float edge = (1.0 - smoothstep(0.5, 1.5, ${(STEM_WIDTH_PX / 2).toFixed(1)} - abs(vAcross))) * step(0.0, vRimSide);
    vec3 col = vec3(${GOLD.map((v) => v.toFixed(2)).join(', ')}) * 0.55 * edge;
    if (uLightOnly > 0.5) { gl_FragColor = vec4(col * cov, 0.0); return; }
    gl_FragColor = vec4(col * cov, cov);
  }
`;

// the receptacle: dark core while full, a small pale ball when bare
const recVertex = `
  uniform vec2 uHead;
  uniform float uHeadR;
  uniform float uAspect;
  varying vec2 vQ;
  void main() { vQ = position.xy; vec2 P = uHead + position.xy * uHeadR * 0.30; gl_Position = vec4(P.x / uAspect, P.y, 0.0, 1.0); }
`;
const recFragment = `
  precision mediump float;
  uniform float uBare;
  uniform float uFade;
  uniform float uPxUnit;
  uniform highp float uHeadR;     // shared with the vertex stage: same precision
  uniform float uLightOnly;
  varying vec2 vQ;
  void main() {
    float r = length(vQ);
    float rr = ${AKENE[0].toFixed(2)} / 0.30;
    float px = 1.0 / (uPxUnit * uHeadR * 0.30);
    float cov = (1.0 - smoothstep(rr - px, rr + px, r)) * uFade;
    float nz = sqrt(max(0.0, 1.0 - (r / rr) * (r / rr)));
    vec3 pale = vec3(0.30, 0.25, 0.17) * (0.5 + 0.5 * nz) + vec3(${GOLD.map((v) => v.toFixed(2)).join(', ')}) * 0.25 * smoothstep(0.6, 0.95, r / rr);
    vec3 col = mix(vec3(0.012, 0.008, 0.004), pale, uBare);
    if (uLightOnly > 0.5) col *= uBare;
    gl_FragColor = vec4(col * cov, cov);
  }
`;

// --- materials: compiled once per tier/kind (pi-shader-compile-cost) ----------
let MATS = null;
function materials() {
  if (MATS) return MATS;
  const detachTex = new THREE.DataTexture(new Uint8Array(MAX_SEEDS * 4), MAX_SEEDS, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  detachTex.minFilter = detachTex.magFilter = THREE.NearestFilter;
  detachTex.generateMipmaps = false; detachTex.needsUpdate = true;
  const U = {
    uHead: { value: new THREE.Vector2(0.9, 0.2) }, uHeadR: { value: HEAD_R }, uSway: { value: 0 }, uTime: { value: 0 },
    uSeeds: { value: 250 }, uDetach: { value: detachTex }, uPxUnit: { value: 360 }, uAspect: safeZoneUniforms.uAspect, uAspectB: safeZoneUniforms.uAspect,
    uFade: { value: 1 }, uLight: { value: new THREE.Vector3(LIGHT[0], LIGHT[1], LIGHT[2]) }, uLightOnly: { value: 0 },
    uTint: { value: new THREE.Color(0, 0, 0) },
    uBokeh: { value: Array.from({ length: 6 }, () => new THREE.Vector4()) },
    uBase: { value: new THREE.Vector2() }, uCtrl: { value: new THREE.Vector2() }, uBare: { value: 0 },
    uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
  };
  // DoubleSide: a line quad's winding on screen depends on the line's direction
  const sh = (vs, fs, extra) => new THREE.ShaderMaterial(Object.assign({ uniforms: U, vertexShader: vs, fragmentShader: fs, depthTest: false, depthWrite: false, side: THREE.DoubleSide }, extra || {}));
  const premul = { transparent: true, blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor };
  const additive = { transparent: true, blending: THREE.AdditiveBlending };
  MATS = {
    U, detachTex,
    bg: sh(bgVertex, bgFragment),
    stem: sh(stemVertex, stemFragment, premul),
    rec: sh(recVertex, recFragment, premul),
    dark: sh(darkVertex, darkFragment, premul),
    pappus: sh(lightVertex(false), lightFragment, additive),
    branches: sh(lightVertex(true), lightFragment, additive)
  };
  return MATS;
}

// a line quad: position.x = side (-1, 1), position.y = along (0, 1)
function lineQuad() {
  const g = new THREE.InstancedBufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0], 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

export class DandelionVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const p = parseFloat(urlParam('dandelionp'));
    this._forcedP = Number.isFinite(p) ? clamp01(p) : null;
    this._lightOnly = urlParam('dandelionlight') === '1';
  }

  // the filaments' brightest strands just reach the bloom: a soft glow
  get postProcessing() { return { bloomStrength: 0.35, bloomThreshold: 0.78 }; }

  init(scene, camera) {
    this._scene = scene;
    const m = materials();
    this._m = m;
    const L = this._layout, n = L.seeds;
    m.U.uSeeds.value = n;
    m.U.uLightOnly.value = this._lightOnly ? 1 : 0;
    this._meshes = [];
    const add = (geo, mat, order) => { const mesh = new THREE.Mesh(geo, mat); mesh.frustumCulled = false; mesh.renderOrder = order; scene.add(mesh); this._meshes.push(mesh); return mesh; };
    // background
    const bgMesh = add(new THREE.PlaneGeometry(2, 2), m.bg, -1000);
    // stem: 48 segments
    const sp = [], si = [];
    for (let k = 0; k <= 48; k++) { const t = k / 48; sp.push(-1, t, 0, 1, t, 0); if (k < 48) { const a = k * 2; si.push(a, a + 1, a + 3, a, a + 3, a + 2); } }
    const sg = new THREE.BufferGeometry(); sg.setAttribute('position', new THREE.Float32BufferAttribute(sp, 3)); sg.setIndex(si);
    add(sg, m.stem, -900);
    add(new THREE.PlaneGeometry(2, 2), m.rec, -890);
    // dark: akene + beak per seed
    const dg = lineQuad();
    const dSeed = new Float32Array(n * 2), dKind = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) { dSeed[i * 2] = dSeed[i * 2 + 1] = i; dKind[i * 2] = 0; dKind[i * 2 + 1] = 1; }
    dg.setAttribute('aSeed', new THREE.InstancedBufferAttribute(dSeed, 1));
    dg.setAttribute('aKind', new THREE.InstancedBufferAttribute(dKind, 1));
    dg.instanceCount = n * 2;
    add(dg, m.dark, -880);
    // pappus: filaments per seed
    const nf = L.filaments, pg = lineQuad();
    const pS = new Float32Array(n * nf), pP = new Float32Array(n * nf), pA = new Float32Array(n * nf), pL = new Float32Array(n * nf);
    for (let i = 0; i < n; i++) for (let k = 0; k < nf; k++) {
      const o = i * nf + k;
      pS[o] = i;
      pP[o] = (PAPPUS_POLAR[0] + (PAPPUS_POLAR[1] - PAPPUS_POLAR[0]) * hash(i, k + 0.3)) * Math.PI / 180;
      pA[o] = (k + 0.4 * hash(i, k + 0.7)) / nf * Math.PI * 2;
      pL[o] = PAPPUS_LEN[0] + (PAPPUS_LEN[1] - PAPPUS_LEN[0]) * hash(i, k + 0.9);
    }
    pg.setAttribute('aSeed', new THREE.InstancedBufferAttribute(pS, 1));
    pg.setAttribute('aPolar', new THREE.InstancedBufferAttribute(pP, 1));
    pg.setAttribute('aAzim', new THREE.InstancedBufferAttribute(pA, 1));
    pg.setAttribute('aLen', new THREE.InstancedBufferAttribute(pL, 1));
    pg.instanceCount = n * nf;
    add(pg, m.pappus, -870);
    // branches (tier 2): two barbs per filament
    if (L.branches) {
      const bgeo = lineQuad();
      const cnt = n * nf * 2;
      const bS = new Float32Array(cnt), bP = new Float32Array(cnt), bA = new Float32Array(cnt), bL = new Float32Array(cnt), bAt = new Float32Array(cnt), bB = new Float32Array(cnt);
      for (let j = 0; j < n * nf; j++) for (let s = 0; s < 2; s++) {
        const o = j * 2 + s;
        bS[o] = pS[j]; bP[o] = pP[j]; bA[o] = pA[j]; bL[o] = pL[j];
        bAt[o] = s === 0 ? 0.55 + 0.1 * hash(j, 1.1) : 0.78 + 0.1 * hash(j, 2.1);
        bB[o] = s === 0 ? 1 : -1;
      }
      bgeo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(bS, 1));
      bgeo.setAttribute('aPolar', new THREE.InstancedBufferAttribute(bP, 1));
      bgeo.setAttribute('aAzim', new THREE.InstancedBufferAttribute(bA, 1));
      bgeo.setAttribute('aLen', new THREE.InstancedBufferAttribute(bL, 1));
      bgeo.setAttribute('aAt', new THREE.InstancedBufferAttribute(bAt, 1));
      bgeo.setAttribute('aBranch', new THREE.InstancedBufferAttribute(bB, 1));
      bgeo.instanceCount = cnt;
      add(bgeo, m.branches, -865);
    }
    const self = this;
    bgMesh.onBeforeRender = function (renderer) { self._layout_(renderer); };

    // detach order: the windward (right) side first, then all round
    const order = [];
    for (let i = 0; i < n; i++) { const d = seedDir(i, n); order.push({ i, key: d[0] + 0.35 * (hash(i, 12) - 0.5) - 0.15 * d[2] }); }
    order.sort((a, b) => b.key - a.key);
    this._order = order.map((o) => o.i);
    this._time = 0; this._t = 0;
    this._progress = null;
    this._lastTrack = undefined;
    this._trackChangedAt = Date.now();
    this._beatSeconds = 60 / FREE_BPM;
    this._energy = 0.4;
    this._fade = 1;
    this._lastPaletteRef = undefined;
    this._resetHead();
    this._newBokeh();
  }

  _resetHead() {
    this._detached = 0;
    this._t = 0;
    const d = this._m.detachTex.image.data;
    d.fill(0);
    this._m.detachTex.needsUpdate = true;
  }

  _newBokeh() {
    const a = this._aspectNow || 16 / 9;
    for (let k = 0; k < 6; k++) {
      const v = this._m.U.uBokeh.value[k];
      // larger and denser toward the light (upper left)
      const x = (-0.95 + 1.5 * Math.random()) * a, y = -0.2 + 1.1 * Math.random();
      v.set(x, y, 0.40 + 0.50 * Math.random(), 0.035 + 0.035 * Math.random());
    }
  }

  _applyPalette(palette) {
    const v = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors[0] : null;
    if (!v) { this._m.U.uTint.value.setRGB(0, 0, 0); return; }
    this._m.U.uTint.value.set(v);
  }

  onTrackChange(profile) { this._applyPalette(profile && profile.artworkPalette); }

  // --- placement (R6): right of the cover, out of the UI zones ---------------------
  _layout_(renderer) {
    const sz = new THREE.Vector2();
    renderer.getDrawingBufferSize(sz);
    const a = sz.x / Math.max(1, sz.y);
    const U = this._m.U;
    U.uPxUnit.value = sz.y / 2;
    if (Math.abs(a - (this._aspectNow || 0)) > 1e-3 || !this._placed) {
      this._aspectNow = a;
      // R19: placed from the stage centre's block, not the cover's position.
      const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;
      const R = HEAD_R + PAPPUS_LEN[1] * HEAD_R * 0.6;            // the head with its pappus
      const marginX = 0.1 / 1 * 1;                                 // 5 % of the height
      let hx = c.x + hs.x + R + 0.08;
      hx = Math.min(hx, a - R - marginX);
      // under the top-right zone (12 % of the height) when the head reaches into its columns
      const topZoneX = a - 0.44 * a;
      let hy = 0.30;
      if (hx + R > topZoneX) hy = Math.min(hy, 1 - 0.24 - R - 0.04);
      hy = Math.max(hy, -0.2);
      this._headBase = [hx, hy];
      this._stemBase = [hx + 0.10, -1.05];
      this._placed = true;
    }
  }

  // --- the frame -------------------------------------------------------------------

  update(deltaTime, profile) {
    if (profile.artworkPalette !== this._lastPaletteRef) { this._lastPaletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    // a new track: a full head fades in over 3 s
    if (profile.trackId !== this._lastTrack) {
      const first = this._lastTrack === undefined;
      this._lastTrack = profile.trackId;
      this._progress = null; this._trackChangedAt = Date.now();
      this._resetHead(); this._newBokeh();
      this._fade = first ? 1 : 0;
    }
    // Pause: frozen, never black (R11).
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt; this._t += dt;
    this._fade = Math.min(1, this._fade + dt / FADE_IN_S);

    let target;
    if (this._forcedP != null) target = this._forcedP;
    else if (profile.duration > 0) target = clamp01(profile.progress || 0);
    else target = ((Date.now() - (profile.trackChangedAt || this._trackChangedAt)) / 1000 / RADIO_LOOP_SECONDS) % 1;
    if (this._progress == null || Math.abs(target - this._progress) > 0.5) this._progress = target;
    else this._progress += (target - this._progress) * (1 - Math.exp(-dt / PROGRESS_TAU));
    const p = this._progress;

    // the energy over 8 bars: a fuller passage lets a few more go, never on a beat
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    this._beatSeconds = 60 / (bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : FREE_BPM);
    const director = profile.director || {};
    const bands = director.hasReliableGrid ? director.bands : null;
    const e = bands ? clamp01(((bands.low || 0) + (bands.mid || 0) + (bands.high || 0)) / 3) : 0.4;
    this._energy += (e - this._energy) * (1 - Math.exp(-dt / (32 * this._beatSeconds)));

    // the count: progress to DONE_AT, the energy bending it a little inside
    // that span (zero at both ends: the total is fixed), never backwards
    const n = this._layout.seeds;
    const q = clamp01(p / DONE_AT);
    const bend = 0.10 * (this._energy - 0.4) * Math.sin(Math.PI * q);
    const want = Math.min(n, Math.floor(n * clamp01(q + bend) + 1e-6));
    if (want > this._detached) {
      const d = this._m.detachTex.image.data;
      // a forced progress (debug) detaches the backlog at staggered past times
      const backlog = want - this._detached;
      for (let k = this._detached; k < want; k++) {
        const i = this._order[k];
        const ago = backlog > 3 ? (want - k) * (this._forcedP != null ? 0.6 : 0.15) : 0;
        const t0 = Math.max(0, Math.round((this._t - ago) * 20));
        d[i * 4] = (t0 >> 8) & 255; d[i * 4 + 1] = t0 & 255; d[i * 4 + 2] = 255; d[i * 4 + 3] = 255;
      }
      this._detached = want;
      this._m.detachTex.needsUpdate = true;
    }
    this._write();
  }

  _write() {
    const U = this._m.U;
    if (!this._headBase) return;
    const n = this._layout.seeds;
    const full = 1 - this._detached / n;
    // the breeze: one slow sway in 12 s, wider while the head is full; the
    // bare stem straightens a little
    const sw = Math.sin(2 * Math.PI * this._time / SWAY_PERIOD_S);
    const amp = 0.012 + 0.030 * full;
    const hx = this._headBase[0] + amp * sw - 0.03 * full, hy = this._headBase[1] + 0.012 * (1 - full);
    U.uHead.value.set(hx, hy);
    U.uSway.value = 0.05 * sw * (0.3 + 0.7 * full);
    const bx = this._stemBase[0], by = this._stemBase[1];
    // the curve's control: bent away from the breeze, less once bare
    U.uBase.value.set(bx, by);
    U.uCtrl.value.set((bx + hx) / 2 + 0.10 * (0.4 + 0.6 * full), (by + hy) / 2);
    U.uTime.value = this._t;
    U.uFade.value = this._fade;
    U.uBare.value = smooth01((1 - full - 0.85) / 0.15);
  }

  get probe() {
    return {
      progress: this._progress, detached: this._detached, seeds: this._layout.seeds, filaments: this._layout.filaments,
      head: this._m.U.uHead.value.toArray(), headR: HEAD_R, t: this._t, fade: this._fade, energy: this._energy,
      bare: this._m.U.uBare.value
    };
  }

  dispose() {
    for (const m of this._meshes) { this._scene.remove(m); m.geometry.dispose(); }
    this._meshes[0].onBeforeRender = null;
    this._meshes = [];
    this._scene = null;
  }
}
